import React, { useState, useEffect, useCallback } from 'react';
import { getNotifications, dismissNotification, addCostCodeNote } from '../api';
import LoadingState from '../components/LoadingState';
import StateScreen from '../components/ui/StateScreen';

/* ═══════════════════════════════════════════════════════════════════════════
   Notifications page — warm-cream + sage redesign
   Top-level route (/notifications): portfolio alert feed (cost overruns +
   schedule problems). Token-only colors. Visual reskin — API + the
   cost-overrun note-required-before-dismiss workflow unchanged.
   ═══════════════════════════════════════════════════════════════════════════ */

const $f = (v) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(+v || 0);

/* ─────────────── Inline SVG icons (matches Icon.js vocabulary) ─────────────── */

const Icon = ({ name, size = 14, sw = 1.6 }) => {
  const s = size;
  const p = (d) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block' }}
    >
      {d}
    </svg>
  );
  switch (name) {
    case 'refresh':
      return p(
        <>
          <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" />
          <path d="M21 3v5h-5" />
          <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" />
          <path d="M3 21v-5h5" />
        </>
      );
    case 'trend-down':
      return p(
        <>
          <path d="M22 17 13.5 8.5 8.5 13.5 2 7" />
          <path d="M16 17h6v-6" />
        </>
      );
    case 'calendar':
      return p(
        <>
          <rect x="3" y="4.5" width="18" height="16.5" rx="2" />
          <path d="M3 9h18M8 2.5v4M16 2.5v4" />
        </>
      );
    case 'clock':
      return p(
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4l3 2" />
        </>
      );
    case 'warn-tri':
      return p(
        <>
          <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
          <path d="M12 9v4M12 17h.01" />
        </>
      );
    case 'check':
      return p(<path d="m5 12 5 5L20 7" />);
    case 'check-circle':
      return p(
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="m8.5 12 2.5 2.5 4.5-5" />
        </>
      );
    case 'arrow-r':
      return p(<path d="M5 12h14M13 6l6 6-6 6" />);
    case 'snooze':
      return p(
        <>
          <circle cx="12" cy="13" r="8" />
          <path d="M12 9v4l2.5 1.5" />
          <path d="M5 3 2 6M19 3l3 3" />
        </>
      );
    case 'spin':
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={sw}
          strokeLinecap="round"
          style={{ display: 'block', animation: 'nSpin .8s linear infinite' }}
        >
          <path d="M21 12a9 9 0 1 1-6.2-8.55" />
        </svg>
      );
    default:
      return null;
  }
};

/* ─────────────── Tone vocabulary ─────────────── */

const TONES = {
  danger: { color: 'var(--danger)', soft: 'var(--danger-soft)' },
  warning: { color: 'var(--warning)', soft: 'var(--warning-soft)' },
  accent: { color: 'var(--accent-text)', soft: 'var(--accent-soft)' },
};
const ring = (toneColor) => `color-mix(in oklch, ${toneColor} 22%, transparent)`;

const TYPE_META = {
  cost_overrun: { label: 'Cost Overrun', tone: 'warning', icon: 'trend-down' },
  schedule_overdue: { label: 'Overdue Task', tone: 'accent', icon: 'calendar' },
  schedule_behind: { label: 'Behind Schedule', tone: 'accent', icon: 'clock' },
};

const Badge = ({ tone, icon, children }) => {
  const t = TONES[tone] || TONES.warning;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: icon ? '2px 9px 2px 7px' : '2px 9px',
        borderRadius: 999,
        fontSize: 12.5,
        fontWeight: 500,
        color: t.color,
        background: t.soft,
        border: `1px solid ${ring(t.color)}`,
        letterSpacing: '-0.005em',
        whiteSpace: 'nowrap',
      }}
    >
      {icon && <Icon name={icon} size={11} sw={2} />}
      {children}
    </span>
  );
};

/* ─────────────── Detail panel ─────────────── */

const DetailItem = ({ label, value, valueColor, mono = true, muted = false }) => (
  <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
    <span style={{ fontSize: 13, color: 'var(--text-3)', letterSpacing: '-0.005em' }}>{label}</span>
    <span
      className={mono ? 'mono' : undefined}
      style={{
        fontSize: 14,
        fontWeight: 600,
        color: muted ? 'var(--text-4)' : valueColor || 'var(--text-1)',
        fontVariantNumeric: mono ? 'tabular-nums' : 'normal',
      }}
    >
      {value}
    </span>
  </div>
);

const Sep = () => <span style={{ color: 'var(--text-4)', fontSize: 13.5 }}>·</span>;

const DetailPanel = ({ alert }) => {
  const d = alert.detail;
  if (!d) return null;
  const isCost = alert.type === 'cost_overrun';
  const items = isCost
    ? [
        <DetailItem key="b" label="Budget" value={$f(d.cost_budget)} />,
        <DetailItem key="a" label="Actual" value={$f(d.cost)} />,
        <DetailItem key="o" label="Overrun" value={$f(d.cost_overrun)} valueColor="var(--danger)" />,
      ]
    : [
        <DetailItem key="due" label="Due" value={d.finish_date || '—'} />,
        <DetailItem key="prog" label="Progress" value={`${d.percent_complete}%`} />,
        d.expected_percent !== undefined && (
          <DetailItem key="exp" label="Expected" value={`${d.expected_percent}%`} valueColor="var(--warning)" />
        ),
        d.days_overdue !== undefined && (
          <DetailItem
            key="over"
            label="Days over"
            value={d.days_overdue}
            valueColor="var(--danger)"
            muted={d.days_overdue === 0}
          />
        ),
        alert.responsible_party && (
          <DetailItem key="who" label="Assigned" value={alert.responsible_party} mono={false} />
        ),
      ].filter(Boolean);
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 10,
        padding: '11px 14px',
        borderRadius: 9,
        background: 'var(--bg-surface-2)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      {items.map((it, i) => (
        <React.Fragment key={i}>
          {i > 0 && <Sep />}
          {it}
        </React.Fragment>
      ))}
    </div>
  );
};

/* ─────────────── Note status + input ─────────────── */

const NoteStatus = ({ hasNote }) =>
  hasNote ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--success)', fontWeight: 500 }}>
      <Icon name="check-circle" size={15} sw={1.8} />
      <span>Note has been added for this cost code.</span>
    </div>
  ) : (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--warning)', fontWeight: 500 }}>
      <Icon name="warn-tri" size={15} sw={1.8} />
      <span>A note explaining this overrun is required before marking as read.</span>
    </div>
  );

const NoteInput = ({ value, onChange, onSave, onCancel, saving }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder="Explain the overrun — root cause, recovery plan, and whether a change order is anticipated…"
        rows={3}
        style={{
          width: '100%',
          resize: 'vertical',
          minHeight: 72,
          padding: '10px 12px',
          borderRadius: 8,
          boxSizing: 'border-box',
          fontFamily: 'var(--font-sans)',
          fontSize: 14.5,
          lineHeight: 1.5,
          color: 'var(--text-1)',
          background: 'var(--bg-surface)',
          border: `1px solid ${focused ? 'var(--accent)' : 'var(--border-default)'}`,
          boxShadow: focused ? '0 0 0 3px var(--accent-ring)' : 'none',
          outline: 'none',
          transition: 'border-color 120ms, box-shadow 120ms',
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !value.trim()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 32,
            padding: '0 13px',
            borderRadius: 7,
            fontSize: 14,
            fontWeight: 500,
            color: '#fff',
            whiteSpace: 'nowrap',
            background: 'var(--accent)',
            border: '1px solid color-mix(in oklch, var(--accent) 80%, oklch(0.2 0.02 135))',
            boxShadow:
              'inset 0 1px 0 oklch(1 0 0 / 0.18), 0 1px 0 color-mix(in oklch, var(--accent) 70%, oklch(0.25 0.04 135))',
            cursor: saving ? 'default' : !value.trim() ? 'not-allowed' : 'pointer',
            opacity: saving || !value.trim() ? 0.7 : 1,
          }}
        >
          {saving ? (
            <>
              <Icon name="spin" size={13} /> Saving…
            </>
          ) : (
            <>
              <Icon name="check" size={13} sw={2.2} /> Add Note & Dismiss
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          style={{
            height: 32,
            padding: '0 12px',
            borderRadius: 7,
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--text-2)',
            whiteSpace: 'nowrap',
            background: 'transparent',
            border: '1px solid var(--border-default)',
            cursor: saving ? 'default' : 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

/* ─────────────── Action button ─────────────── */

const ActionBtn = ({ variant = 'ghost', icon, children, onClick, disabled, busy }) => {
  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 32,
    padding: '0 13px',
    borderRadius: 7,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    cursor: disabled ? 'default' : 'pointer',
    transition: 'background 120ms, border-color 120ms, color 120ms',
  };
  const variants = {
    primary: {
      color: 'var(--accent-text)',
      background: 'var(--accent-soft)',
      border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)',
    },
    ghost: {
      color: 'var(--text-2)',
      background: 'var(--bg-surface)',
      border: '1px solid var(--border-default)',
      boxShadow: 'var(--shadow-xs)',
    },
    ghostAccent: {
      color: 'var(--accent-text)',
      background: 'var(--bg-surface)',
      border: '1px solid var(--border-default)',
      boxShadow: 'var(--shadow-xs)',
    },
  };
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`n-action n-action-${variant}`}
      style={{ ...base, ...variants[variant], opacity: disabled && !busy ? 0.5 : 1 }}
    >
      {busy ? <Icon name="spin" size={13} /> : icon && <Icon name={icon} size={13} sw={variant === 'primary' ? 2 : 1.7} />}
      {children}
    </button>
  );
};

/* ─────────────── Alert card (core) ─────────────── */

const AlertCard = ({ alert, onDismiss, onSnooze, onOpenProject }) => {
  const [noteText, setNoteText] = useState('');
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [dismissing, setDismissing] = useState(false);
  const [snoozing, setSnoozing] = useState(false);

  const isCost = alert.type === 'cost_overrun';
  const meta = TYPE_META[alert.type] || TYPE_META.cost_overrun;
  const typeToneColor = TONES[meta.tone].color;
  const stripeTone = alert.severity === 'critical' ? 'var(--danger)' : 'var(--warning)';
  const locked = dismissing || snoozing || submitting;

  const handleMarkRead = async () => {
    if (dismissing) return;
    // Cost overrun with no note → open the note input instead of dismissing.
    if (isCost && !alert.has_note && !showNoteInput) {
      setShowNoteInput(true);
      return;
    }
    setError(null);
    try {
      setDismissing(true);
      await onDismiss(alert.id, 'read');
    } catch (err) {
      if (err.message && err.message.includes('note')) {
        setShowNoteInput(true);
        setError('A note explaining the overrun is required first.');
      } else {
        setError(err.message);
      }
    } finally {
      setDismissing(false);
    }
  };

  const handleSnooze = async () => {
    if (snoozing) return;
    try {
      setSnoozing(true);
      await onSnooze(alert.id);
    } catch (err) {
      setError(err?.message || 'Failed to snooze');
    } finally {
      setSnoozing(false);
    }
  };

  const handleSubmitNote = async () => {
    if (submitting) return;
    if (!noteText.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await addCostCodeNote({
        job_id: alert.job_id,
        phase: alert.phase,
        category: alert.category || '',
        note: noteText.trim(),
      });
      setNoteText('');
      setShowNoteInput(false);
      await onDismiss(alert.id, 'read');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <article
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        borderRadius: 12,
        boxShadow: `inset 3px 0 0 ${stripeTone}, var(--shadow-sm)`,
        padding: '16px 20px 16px 22px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        opacity: locked ? 0.92 : 1,
        transition: 'opacity 150ms',
      }}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 30,
            height: 30,
            flex: '0 0 auto',
            borderRadius: 8,
            color: typeToneColor,
            background: TONES[meta.tone].soft,
            border: `1px solid color-mix(in oklch, ${typeToneColor} 18%, transparent)`,
          }}
        >
          <Icon name={meta.icon} size={16} />
        </span>
        <Badge tone={meta.tone}>{meta.label}</Badge>
        {alert.severity === 'critical' ? (
          <Badge tone="danger" icon="warn-tri">
            Critical
          </Badge>
        ) : (
          <Badge tone="warning" icon="warn-tri">
            Warning
          </Badge>
        )}
        <div style={{ flex: 1 }} />
        <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {alert.created_at}
        </span>
      </div>

      {/* Project line */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => alert.project_id && onOpenProject(alert.project_id)}
          className="n-project-link"
          style={{
            fontSize: 15.5,
            fontWeight: 600,
            color: 'var(--accent-text)',
            letterSpacing: '-0.01em',
            background: 'none',
            border: 0,
            padding: 0,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {alert.project_name}
        </button>
        {alert.project_number && (
          <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            #{alert.project_number}
          </span>
        )}
        {alert.project_manager && (
          <>
            <span style={{ color: 'var(--text-4)' }}>·</span>
            <span style={{ fontSize: 13, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
              PM <span style={{ color: 'var(--text-2)' }}>{alert.project_manager}</span>
            </span>
          </>
        )}
      </div>

      {/* Message */}
      <p style={{ margin: 0, fontSize: 15, color: 'var(--text-1)', lineHeight: 1.5, letterSpacing: '-0.005em' }}>
        {alert.message}
      </p>

      {/* Detail panel */}
      <DetailPanel alert={alert} />

      {/* Note status (cost overruns only) */}
      {isCost && !showNoteInput && <NoteStatus hasNote={alert.has_note} />}

      {/* Inline error */}
      {error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            padding: '9px 12px',
            borderRadius: 8,
            background: 'var(--danger-soft)',
            boxShadow: 'inset 3px 0 0 var(--danger)',
            fontSize: 14,
            color: 'var(--danger)',
            fontWeight: 500,
            lineHeight: 1.45,
          }}
        >
          <span style={{ flex: '0 0 auto', marginTop: 1 }}>
            <Icon name="warn-tri" size={14} sw={1.8} />
          </span>
          <span>{error}</span>
        </div>
      )}

      {/* Note input OR action buttons */}
      {showNoteInput ? (
        <NoteInput
          value={noteText}
          onChange={setNoteText}
          onSave={handleSubmitNote}
          onCancel={() => {
            setShowNoteInput(false);
            setError(null);
          }}
          saving={submitting}
        />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <ActionBtn variant="primary" icon="check" onClick={handleMarkRead} disabled={locked} busy={dismissing}>
            {dismissing ? 'Marking…' : 'Mark as Read'}
          </ActionBtn>
          <ActionBtn variant="ghost" icon="snooze" onClick={handleSnooze} disabled={locked} busy={snoozing}>
            {snoozing ? 'Snoozing…' : 'Snooze 3 Days'}
          </ActionBtn>
          {alert.project_id && (
            <ActionBtn variant="ghostAccent" icon="arrow-r" onClick={() => onOpenProject(alert.project_id)} disabled={locked}>
              Open Project
            </ActionBtn>
          )}
        </div>
      )}
    </article>
  );
};

/* ─────────────── Region 1 — summary header ─────────────── */

const SummaryHeader = ({ summary, onRefresh }) => (
  <header style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '0 2px' }}>
    <div style={{ flex: 1, minWidth: 0, fontSize: 15, color: 'var(--text-2)', letterSpacing: '-0.005em' }}>
      <span className="mono" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--text-1)' }}>
        {summary.total || 0}
      </span>{' '}
      active alert{summary.total === 1 ? '' : 's'}
      {summary.requiring_note > 0 && (
        <>
          <span style={{ color: 'var(--text-4)', margin: '0 8px' }}>·</span>
          <span style={{ color: 'var(--warning)', fontWeight: 600 }}>
            <span className="mono" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {summary.requiring_note}
            </span>{' '}
            need a note
          </span>
        </>
      )}
    </div>
    <button
      type="button"
      className="n-refresh"
      onClick={onRefresh}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 32,
        padding: '0 12px',
        borderRadius: 7,
        fontSize: 14,
        fontWeight: 500,
        color: 'var(--text-2)',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        boxShadow: 'var(--shadow-xs)',
        cursor: 'pointer',
      }}
    >
      <Icon name="refresh" size={13} /> Refresh
    </button>
  </header>
);

/* ─────────────── Region 2 — KPI row ─────────────── */

const KpiTile = ({ label, value, valueColor, muted, fill, ringColor }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      flex: '1 1 160px',
      minWidth: 150,
      padding: '16px 20px',
      borderRadius: 10,
      background: fill || 'transparent',
      border: ringColor ? `1px solid ${ringColor}` : '1px solid transparent',
    }}
  >
    <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
      {label}
    </div>
    <div
      className="mono"
      style={{
        fontSize: 29.5,
        fontWeight: 800,
        lineHeight: 1,
        color: muted ? 'var(--text-4)' : valueColor || 'var(--text-1)',
        letterSpacing: '-0.03em',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </div>
  </div>
);

const KpiDivider = () => (
  <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border-subtle)', margin: '10px 0', flex: '0 0 auto' }} />
);

const KpiRow = ({ summary }) => {
  const s = summary;
  return (
    <section
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        padding: 6,
        gap: 0,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        borderRadius: 12,
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <KpiTile
        label="Total Alerts"
        value={s.total || 0}
        valueColor="var(--accent-text)"
        muted={!s.total}
        fill={!s.total ? null : 'var(--accent-soft)'}
        ringColor={!s.total ? null : ring('var(--accent)')}
      />
      <KpiDivider />
      <KpiTile label="Schedule" value={s.schedule || 0} valueColor="var(--text-1)" muted={!s.schedule} />
      <KpiDivider />
      <KpiTile
        label="Cost Overruns"
        value={s.cost || 0}
        valueColor="var(--warning)"
        muted={!s.cost}
        fill={!s.cost ? null : 'var(--warning-soft)'}
        ringColor={!s.cost ? null : ring('var(--warning)')}
      />
      <KpiDivider />
      <KpiTile
        label="Critical"
        value={s.critical || 0}
        valueColor="var(--danger)"
        muted={!s.critical}
        fill={!s.critical ? null : 'var(--danger-soft)'}
        ringColor={!s.critical ? null : ring('var(--danger)')}
      />
    </section>
  );
};

/* ─────────────── Region 3 — filter chips ─────────────── */

const FILTERS = [
  { key: 'all', label: 'All', countKey: 'total' },
  { key: 'schedule', label: 'Schedule', countKey: 'schedule' },
  { key: 'cost', label: 'Cost Overruns', countKey: 'cost', dot: 'var(--warning)' },
  { key: 'critical', label: 'Critical', countKey: 'critical', dot: 'var(--danger)' },
];

const FilterChips = ({ active, onChange, summary }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '0 2px' }}>
    {FILTERS.map((f) => {
      const count = summary[f.countKey] || 0;
      const isActive = f.key === active;
      const showDot = f.dot && count > 0;
      return (
        <button
          key={f.key}
          type="button"
          onClick={() => onChange(f.key)}
          className="n-chip"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            height: 32,
            padding: isActive ? '0 11px' : '0 12px',
            borderRadius: 999,
            fontSize: 14,
            fontWeight: isActive ? 600 : 500,
            whiteSpace: 'nowrap',
            color: isActive ? 'var(--accent-text)' : 'var(--text-3)',
            background: isActive ? 'var(--accent-soft)' : 'var(--bg-surface)',
            border: isActive ? '2px solid var(--accent)' : '1px solid var(--border-default)',
            cursor: 'pointer',
            transition: 'background 120ms, color 120ms, border-color 120ms',
          }}
        >
          {showDot && <span style={{ width: 6, height: 6, borderRadius: 999, background: f.dot, flex: '0 0 auto' }} />}
          {f.label}
          <span
            className="mono"
            style={{
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 999,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              color: isActive ? 'var(--accent-text)' : 'var(--text-3)',
              background: isActive ? 'color-mix(in oklch, var(--accent) 16%, transparent)' : 'var(--bg-surface-2)',
            }}
          >
            {count}
          </span>
        </button>
      );
    })}
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════════════════ */

const FILTER_LABEL = { all: '', schedule: 'schedule', cost: 'cost overrun', critical: 'critical' };

export default function NotificationsPage({ onOpenProject }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all'); // all | schedule | cost | critical

  const fetchAlerts = useCallback(async () => {
    try {
      const result = await getNotifications();
      setData(result);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleDismiss = async (alertKey, action) => {
    await dismissNotification({ alert_key: alertKey, action });
    fetchAlerts();
  };

  const handleSnooze = async (alertKey) => {
    await dismissNotification({ alert_key: alertKey, action: 'snoozed' });
    fetchAlerts();
  };

  if (loading) return <LoadingState message="Loading notifications…" />;

  const alerts = data?.alerts || [];
  const summary = data?.summary || {};

  const filtered = alerts.filter((a) => {
    if (filter === 'schedule') return a.type.startsWith('schedule_');
    if (filter === 'cost') return a.type === 'cost_overrun';
    if (filter === 'critical') return a.severity === 'critical';
    return true;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`
        @keyframes nSpin { to { transform: rotate(360deg); } }
        .n-refresh:hover {
          border-color: color-mix(in oklch, var(--accent) 35%, var(--border-default)) !important;
          color: var(--accent-text) !important;
          background: var(--accent-soft) !important;
        }
        .n-chip:hover { border-color: color-mix(in oklch, var(--accent) 30%, var(--border-default)); }
        .n-project-link:hover { text-decoration: underline; }
        .n-action-ghost:hover:not(:disabled) { background: var(--bg-hover) !important; }
        .n-action-ghostAccent:hover:not(:disabled) { background: var(--accent-soft) !important; border-color: color-mix(in oklch, var(--accent) 30%, var(--border-default)) !important; }
        .n-action-primary:hover:not(:disabled) { background: color-mix(in oklch, var(--accent) 14%, var(--accent-soft)) !important; }
      `}</style>

      <SummaryHeader summary={summary} onRefresh={fetchAlerts} />
      <KpiRow summary={summary} />
      <FilterChips active={filter} onChange={setFilter} summary={summary} />

      {filtered.length === 0 ? (
        <StateScreen
          kind="empty"
          title={filter === 'all' ? 'No active alerts' : `No ${FILTER_LABEL[filter]} alerts`}
          body="All clear — nothing needs your attention right now."
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {filtered.map((alert) => (
            <AlertCard
              key={alert.id}
              alert={alert}
              onDismiss={handleDismiss}
              onSnooze={handleSnooze}
              onOpenProject={onOpenProject}
            />
          ))}
        </div>
      )}
    </div>
  );
}
